import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    Typography,
    Slide,
    Paper,
    Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Icon } from '@iconify/react/dist/iconify.js';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const ViewGateKeeperModal = ({ open, handleClose, data = null }) => {
    if (!data) return null;

    const InfoRow = ({ icon, label, value, chip = false, chipColor = 'default' }) => (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
                <Icon icon={icon} width={18} style={{ color: '#64748b' }} />
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {label}
                </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
                {chip ? (
                    <Chip
                        label={value}
                        size="small"
                        color={chipColor}
                        sx={{ fontWeight: 500 }}
                    />
                ) : (
                    <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
                        {value || '-'}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                }
            }}
        >
            {/* Header with Gradient Background */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    position: 'relative',
                }}
            >
                {/* Background Pattern */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: 0.1,
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}
                />

                <DialogTitle sx={{ p: 2.5, pb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    backgroundColor: 'rgba(255,255,255,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Icon icon="mdi:account-details" width={24} height={24} />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1.1rem' }}>
                                    GateKeeper Details
                                </Typography>
                                <p className='tw-text-white tw-text-sm'>
                                    View gatekeeper login information
                                </p>
                            </Box>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            size="small"
                            sx={{
                                color: 'white',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.25)',
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </DialogTitle>
            </Box>

            <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
                <Box sx={{ p: 2.5 }}>
                    {/* GateKeeper Info Card */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper',
                            mb: 2,
                        }}
                    >
                        {/* Header with ID and Status */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, pb: 2, borderBottom: '2px solid', borderColor: 'divider' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 50,
                                        height: 50,
                                        borderRadius: 2,
                                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Icon icon="mdi:account-key" width={28} style={{ color: 'white' }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                        {data.login}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        ID: #{data.id}
                                    </Typography>
                                </Box>
                            </Box>
                            <Chip
                                icon={<Icon icon={data.status ? "mdi:check-circle" : "mdi:close-circle"} width={16} />}
                                label={data.status ? 'Active' : 'Inactive'}
                                color={data.status ? 'success' : 'default'}
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>

                        {/* Details */}
                        <InfoRow icon="mdi:office-building" label="Society" value={data.society} />
                        <InfoRow icon="mdi:phone" label="Phone" value={data.phone} />
                        <InfoRow icon="mdi:calendar" label="Issue Date" value={data.issueDate} />
                        <InfoRow icon="mdi:note-text" label="Remark" value={data.remark} />
                    </Paper>
                </Box>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
                <button
                    type="button"
                    onClick={handleClose}
                    className="btn btn-outline-secondary radius-8 px-20 py-10 text-sm fw-medium"
                    style={{ flex: 1 }}
                >
                    <Icon icon="solar:close-circle-outline" className="me-6" />
                    Close
                </button>
            </DialogActions>
        </Dialog>
    );
};

export default ViewGateKeeperModal;
